import React, { Component, Fragment, useState } from 'react'
import { array, object, bool, oneOf } from 'prop-types'
import throttle from 'lodash.throttle'
import { createComponent, withTheme, ThemeProvider } from '@ycos/fela'
import { Grid } from '@ycos/component-grid'
import { hasTag } from '@ycos/coremedia-renderer'
import SpanContainer from './SpanContainer'
import { areAllChildrenImages, hasColumnOnlyWithImages } from './utils'

const MIN_HEIGHT_DIFF = 88

const StyledRule = createComponent(
  'hr',
  ({ theme }) => ({
    margin: 0,
    border: 0,
    height: '1px',
    background: theme.custom.divider.color
  }),
  'hr'
)

const StyledRuleWrapper = createComponent('div', ({ theme }) => ({
  paddingTop: `${6 * theme.spacingMultiplier}px`,
  paddingBottom: `${6 * theme.spacingMultiplier}px`,
  'screen-medium': {
    display: 'none'
  }
}))

const HorizontalDivider = () => (
  <StyledRuleWrapper>
    <StyledRule />
  </StyledRuleWrapper>
)

const desktopReverseStyles = {
  'screen-medium': {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end'
  }
}

const FullBleedWrapper = createComponent('FullBleedWrapper', () => ({
  display: 'block',
  width: '100%',
  'screen-medium': {
    display: 'flex',
    justifyContent: 'space-between'
  }
}))
const FullBleedItem = createComponent('FullBleedItem', ({ prominence }) => ({
  display: 'block',
  width: '100%',
  'screen-medium': {
    width: prominence ? '55%' : '40%'
  }
}))

class TwoColumnWithProminence extends Component {
  static layoutVariant = ['isc-two-column-left-prominence', 'isc-two-column-right-prominence']

  static getGridSettings(prominence, areAllImages) {
    return {
      small: { offset: 0, width: 6 },
      medium: {
        prominent: {
          offset: prominence === 'left' || !areAllImages ? 1 : 0,
          width: 6
        },
        notProminent: {
          offset: prominence === 'right' || !areAllImages ? 1 : 0,
          width: areAllImages ? 4 : 3
        }
      }
    }
  }

  static getRowStyles(prominence, reverseColumns) {
    return (prominence === 'left' && reverseColumns) || (prominence === 'right' && !reverseColumns) ? desktopReverseStyles : {}
  }

  static hasStickProp(props) {
    const { tags } = props.item
    return props.isSticky || hasTag(tags, 'Columns.Sticky')
  }

  constructor(props) {
    super(props)
    this.state = {
      isSticky: TwoColumnWithProminence.hasStickProp(props)
    }
    this.handleScroll = throttle(this.handleScroll, 1000).bind(this)
  }

  componentDidMount() {
    const sticky = TwoColumnWithProminence.hasStickProp(this.props)
    const nav = window.document.querySelector("[data-component='Navigation']")
    this.navHeight = nav ? nav.clientHeight : 0

    if (sticky) {
      window.addEventListener('scroll', this.handleScroll)
    }
  }

  componentWillReceiveProps(nextProps) {
    const sticky = TwoColumnWithProminence.hasStickProp(this.props)
    const nextSticky = TwoColumnWithProminence.hasStickProp(nextProps)

    if (sticky !== nextSticky) {
      if (nextSticky) {
        this.handleScroll()
        window.addEventListener('scroll', this.handleScroll)
      } else {
        window.removeEventListener('scroll', this.handleScroll)
        this.setState({
          isSticky: false
        })
      }
    }
  }

  componentWillUnmount() {
    const sticky = TwoColumnWithProminence.hasStickProp(this.props)
    if (sticky) {
      window.removeEventListener('scroll', this.handleScroll)
    }
  }

  handleScroll() {
    const heightDifference = Math.abs(this.column0.clientHeight - this.column1.clientHeight)
    this.setState({ isSticky: heightDifference > MIN_HEIGHT_DIFF })
  }

  render() {
    const { isSticky } = this.state
    const { children, item, fullBleed, viewportHeight, noPaddingTop, noPaddingTopMobile, addPaddingBottom, addPaddingBottomMobile, extraStyles, theme, gridSettings } = this.props
    const { layoutVariant, tags } = item
    const allImages = areAllChildrenImages(children)
    const desktopReverse = this.props.desktopReverse || hasTag(tags, 'Columns.DesktopReverse')
    const verticallyCenter = this.props.verticallyCenter || hasTag(tags, 'Columns.VerticallyCenter')
    const dividerLine = this.props.dividerLine || hasTag(tags, 'Columns.DividerLine')
    const noTopPadding = noPaddingTop || hasTag(item && item.tags, 'Columns.NoTopPadding')
    const noTopPaddingMobile = noPaddingTopMobile || hasTag(item && item.tags, 'Columns.NoTopPaddingMobile')
    const addBottomPadding = addPaddingBottom || hasTag(item && item.tags, 'Columns.AddBottomPadding')
    const addBottomPaddingMobile = addPaddingBottomMobile || hasTag(item && item.tags, 'Columns.AddBottomPaddingMobile')
    const vhHeight = viewportHeight || hasTag(item && item.tags, 'Columns.ViewportHeight')

    const hasDivider = !verticallyCenter && !allImages && dividerLine
    // If the prominence prop is set then it will override the value from layout variant
    const prominence = this.props.prominence || (layoutVariant.indexOf('left') > -1 ? 'left' : 'right')
    const { small, medium } = Object.keys(gridSettings).length ? gridSettings : TwoColumnWithProminence.getGridSettings(prominence, allImages)
    const rowStyles = TwoColumnWithProminence.getRowStyles(prominence, desktopReverse)
    const dividerOnMobile = !hasColumnOnlyWithImages(children)

    // For a column to be flush to top, that column must have the noPaddingTop tag
    const firstColumnComponent = children[0]?.props.item
    const firstColumnIsFlush = hasTag(firstColumnComponent?.tags, 'Columns.NoTopPadding') || noTopPadding
    const firstColumnIsFlushMobile = hasTag(firstColumnComponent?.tags, 'Columns.NoTopPaddingMobile') || noTopPaddingMobile

    const secondColumnComponent = children[1]?.props.item
    const secondColumnIsFlush = hasTag(secondColumnComponent?.tags, 'Columns.NoTopPadding') || noTopPadding
    const secondColumnIsFlushMobile = hasTag(secondColumnComponent?.tags, 'Columns.NoTopPaddingMobile') || noTopPaddingMobile
    const FullBleedLayout = (
      <Grid.Row additionalStyles={{ ...rowStyles, ...extraStyles }} verticallyCenter={verticallyCenter} fullBleed={fullBleed}>
        <FullBleedWrapper>
          <FullBleedItem prominence={prominence === 'left'}>
            <SpanContainer
              noPaddingTop={firstColumnIsFlush}
              noPaddingTopMobile={firstColumnIsFlushMobile}
              addPaddingBottom={addBottomPadding}
              addPaddingBottomMobile={addBottomPaddingMobile}
              isSticky={isSticky}
              offsetTop={this.navHeight}
              centerContent={verticallyCenter}
              viewportHeight={vhHeight}
              innerRef={(element) => (this.column0 = element)}
            >
              <Fragment>
                <ThemeProvider theme={theme}>{children[0]}</ThemeProvider>
                {dividerLine && dividerOnMobile && <HorizontalDivider />}
              </Fragment>
            </SpanContainer>
          </FullBleedItem>
          <FullBleedItem prominence={prominence === 'right'}>
            <SpanContainer
              noPaddingTop={secondColumnIsFlush}
              noPaddingTopMobile={secondColumnIsFlushMobile}
              addPaddingBottom={addBottomPadding}
              addPaddingBottomMobile={addBottomPaddingMobile}
              isSticky={isSticky}
              offsetTop={this.navHeight}
              centerContent={verticallyCenter}
              viewportHeight={vhHeight}
              innerRef={(element) => (this.column1 = element)}
            >
              <ThemeProvider theme={theme}>{children[1]}</ThemeProvider>
            </SpanContainer>
          </FullBleedItem>
        </FullBleedWrapper>
      </Grid.Row>
    )

    const NormalLayout = (
      <Grid.Row additionalStyles={{ ...rowStyles, ...extraStyles }} verticallyCenter={verticallyCenter}>
        <Grid.Span small={small} medium={desktopReverse ? medium.notProminent : medium.prominent} divider={desktopReverse && hasDivider ? prominence : null}>
          <SpanContainer
            noPaddingTop={firstColumnIsFlush}
            noPaddingTopMobile={firstColumnIsFlushMobile}
            addPaddingBottom={addBottomPadding}
            addPaddingBottomMobile={addBottomPaddingMobile}
            isSticky={isSticky}
            offsetTop={this.navHeight}
            centerContent={verticallyCenter}
            viewportHeight={vhHeight}
            innerRef={(element) => (this.column0 = element)}
          >
            <Fragment>
              <ThemeProvider theme={theme}>{children[0]}</ThemeProvider>
              {dividerLine && dividerOnMobile && <HorizontalDivider />}
            </Fragment>
          </SpanContainer>
        </Grid.Span>
        <Grid.Span small={small} medium={desktopReverse ? medium.prominent : medium.notProminent} divider={!desktopReverse && hasDivider ? prominence : null}>
          <SpanContainer
            noPaddingTop={secondColumnIsFlush}
            noPaddingTopMobile={secondColumnIsFlushMobile}
            addPaddingBottom={addBottomPadding}
            addPaddingBottomMobile={addBottomPaddingMobile}
            isSticky={isSticky}
            offsetTop={this.navHeight}
            centerContent={verticallyCenter}
            viewportHeight={vhHeight}
            innerRef={(element) => (this.column1 = element)}
          >
            <ThemeProvider theme={theme}>{children[1]}</ThemeProvider>
          </SpanContainer>
        </Grid.Span>
      </Grid.Row>)
    const TwoColumnWithProminenceLayout = fullBleed ? FullBleedLayout : NormalLayout
    return <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>{TwoColumnWithProminenceLayout}</ThemeProvider>
  }
}

TwoColumnWithProminence.propTypes = {
  children: array,
  item: object,
  desktopReverse: bool,
  fullBleed: bool,
  verticallyCenter: bool,
  dividerLine: bool,
  prominence: oneOf(['left', 'right']),
  isSticky: bool, // eslint-disable-line
  viewportHeight: bool,
  noPaddingTop: bool,
  noPaddingTopMobile: bool,
  addPaddingBottom: bool,
  addPaddingBottomMobile: bool,
  gridSettings: object
}

TwoColumnWithProminence.defaultProps = {
  children: [],
  item: {},
  desktopReverse: false,
  fullBleed: false,
  verticallyCenter: false,
  dividerLine: false,
  isSticky: false,
  viewportHeight: false,
  noPaddingTop: false,
  noPaddingTopMobile: false,
  addPaddingBottom: false,
  addPaddingBottomMobile: false,
  gridSettings: {}
}

export default withTheme(TwoColumnWithProminence)
